import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  SettingRow,
  SettingsGroup,
  Skeleton,
  Switch,
} from "@playbookai/ui";
import { useAuthStore } from "../../auth/auth-store.js";
import {
  sameSession,
  scopeFromClaims,
  type SessionScope,
} from "../../auth/session-scope.js";
import { useLeaveGuard } from "../../settings/leave-guard.js";
import {
  INVITE_LIFETIME_MAX,
  INVITE_LIFETIME_MIN,
  INVITE_LIFETIME_STEP,
  accessPolicyQueryKey,
  getAccessPolicy,
  isPolicyRevisionConflict,
  updateAccessPolicy,
  validateInvitationLifetime,
  type AccessPolicy,
} from "../../api/access-policy-api.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import { AdminTimeZoneLabel } from "./AdminTimeZoneLabel.js";

/* Workspace access policy panel (PB-4826).

   PB-2926 shipped three enforced controls, an endpoint and a migration that
   backfills existing tenants with local invitations DISABLED — and no screen. On
   merge that closes the password-account invitation route with no way to reopen it
   short of a direct database write. This panel is the way back.

   Hosted on Members & roles rather than a nav section of its own: the policy
   governs how people are added, and the two controls it constrains — the invite
   dialog's route choice and the create-account button — are on this page already.

   Admin-only via the `/admin` parent route gate (RequireTenantAdmin, PB-48); the
   server remains the authoritative gate. */

/** The three controls, with the lifetime held as typed text so a half-entered
    value is never coerced into a number the administrator did not choose. */
interface PolicyDraft {
  localAccountsEnabled: boolean;
  localInvitationsEnabled: boolean;
  invitationLifetimeHours: string;
}

/** A draft plus the session that authored it, so an edit can never outlive it —
    the same fence `OrganizationAdmin` uses, for the same reason: `/admin` stays
    mounted across an in-place session replacement, so without the stamp one
    administrator's unsaved security policy is on screen for the next to save. */
interface ScopedDraft {
  readonly scope: SessionScope;
  /**
   * The revision the draft was derived FROM, captured when editing starts and
   * preserved across every later keystroke in the same session.
   *
   * NOT read from the query at submit time, which is what an earlier revision of
   * this file did and what review caught. The conflict handler below invalidates
   * this query on purpose, so a refetch can replace the snapshot while the draft
   * sits unchanged; a save that then read the snapshot would present a revision
   * the administrator was never shown, the server would accept it, and the other
   * administrator's change would be silently overwritten — the exact outcome the
   * precondition exists to prevent, opened by the code meant to enforce it.
   */
  readonly revision: number;
  readonly policy: PolicyDraft;
}

function draftFromPolicy(policy: AccessPolicy): PolicyDraft {
  return {
    localAccountsEnabled: policy.localAccountsEnabled,
    localInvitationsEnabled: policy.localInvitationsEnabled,
    invitationLifetimeHours: String(policy.invitationLifetimeHours),
  };
}

/**
 * The tenant's access policy, for the surfaces that must not offer a route it
 * forbids.
 *
 * One shared query key, so the panel, the invite dialog and the create-account
 * button read a single fetch rather than three. Exported the way `InvitesPanel`
 * exports `useInviteDialog` — a panel owning both a section and the hook its page
 * needs.
 *
 * THE STATUS IS PART OF THE ANSWER, and callers must not collapse it. An earlier
 * revision returned only `data` and documented every consumer as failing OPEN,
 * on the reasoning that these are conveniences over a server that already
 * refuses. Review found that reasoning is only half true, and the half that is
 * false is the dangerous one:
 *
 *   - The INVITATION route is server-enforced (`assertRoutePermitted`), so
 *     offering it while the policy is unknown costs a refused request and
 *     nothing else. Failing open there is right.
 *   - Local-account CREATION is server-enforced too, but only since PB-4922
 *     (AC-16) — `provisionLocalUser` had no policy read at all, so for the whole
 *     life of this panel the gate below was the ONLY thing standing between an
 *     administrator and an account that gets created and then refused at its
 *     first sign-in.
 *
 * The conclusion the second bullet reached does NOT relax now that the server
 * refuses as well, and it is worth saying why: a fail-open gate would offer the
 * action, take the administrator through the dialog, and hand them a 403 at the
 * end. The server refusal is the authority; this gate is what stops the console
 * from proposing work it knows cannot succeed. So a consumer must still require
 * an affirmative read (`policy?.localAccountsEnabled === true`) and use
 * `isPending` / `isError` to say why it cannot act yet.
 */
export interface AccessPolicyRead {
  /** Undefined while pending and after a failure — never a default. */
  readonly policy: AccessPolicy | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly isFetching: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
}

/**
 * THE ONLY definition of this query. The panel consumes it too rather than
 * declaring its own `useQuery` on the same key.
 *
 * Two observers on one key is not merely duplication: TanStack fetches with one
 * observer's options, so a `retry` or `staleTime` set on one of them silently
 * governs the other, and which one wins depends on mount order. Verified during
 * review that the split also made the failure state unobservable in the running
 * app — one request went out, nothing retried, and the query sat in `pending`
 * while both surfaces rendered "checking" indefinitely.
 *
 * `retry: false` because this read gates an action. A policy that cannot be read
 * has to say so quickly so the administrator can retry deliberately; silently
 * re-attempting behind a spinner leaves a disabled button with no explanation,
 * which is the state review found.
 */
export function useAccessPolicy(): AccessPolicyRead {
  const claims = useAuthStore((s) => s.claims);
  const query = useQuery({
    queryKey: accessPolicyQueryKey(claims?.tenantId ?? null),
    queryFn: getAccessPolicy,
    retry: false,
  });
  return {
    policy: query.data,
    isPending: query.isPending,
    isError: query.isError,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

export function AccessPolicyPanel(): ReactElement {
  const formatter = useDateTimeFormatter();
  const { dateTimeOr } = formatter;
  const claims = useAuthStore((s) => s.claims);
  const tenantId = claims?.tenantId ?? null;
  const scope = scopeFromClaims(claims);
  const queryClient = useQueryClient();

  const policyQuery = useAccessPolicy();
  const policy = policyQuery.policy;

  const savedDraft = policy ? draftFromPolicy(policy) : null;
  /* null means untouched: the fields show fetched values until the admin edits,
     so a slow fetch is never mistaken for a chosen setting. Derived rather than
     cleared in an effect, so a stale draft is never rendered — not even for the
     frame before the effect would run. */
  const [draft, setDraft] = useState<ScopedDraft | null>(null);
  /* The whole scoped draft, so a submit carries the revision it was derived from
     rather than reconstructing one. `activeDraft` stays the policy half, which is
     what every render below reads. */
  const activeScoped =
    draft !== null && sameSession(draft.scope, scope) ? draft : null;
  const activeDraft = activeScoped?.policy ?? null;
  const current = activeDraft ?? savedDraft;

  const lifetimeError = activeDraft
    ? validateInvitationLifetime(activeDraft.invitationLifetimeHours)
    : null;
  const changed =
    activeDraft !== null &&
    savedDraft !== null &&
    (activeDraft.localAccountsEnabled !== savedDraft.localAccountsEnabled ||
      activeDraft.localInvitationsEnabled !==
        savedDraft.localInvitationsEnabled ||
      activeDraft.invitationLifetimeHours.trim() !==
        savedDraft.invitationLifetimeHours);

  const edit = (patch: Partial<PolicyDraft>): void => {
    if (!savedDraft || !policy) return;
    /* A new edit retires the previous save's failure. Without this the Alert from
       a refused save — including the conflict Alert, whose whole message is "the
       values above now show what is actually saved" — stays on screen describing
       an attempt the administrator has already moved past, and it would still be
       there when they press Save again. Review caught it. */
    save.reset();
    setDraft((currentDraft) => {
      /* The draft being CONTINUED, or null when this keystroke starts a fresh
         one — including after a session change, which must re-derive from the
         policy now on screen rather than inherit a revision the previous
         administrator read. */
      const continued =
        currentDraft !== null && sameSession(currentDraft.scope, scope)
          ? currentDraft
          : null;
      return {
        scope,
        revision: continued?.revision ?? policy.revision,
        policy: { ...(continued?.policy ?? savedDraft), ...patch },
      };
    });
  };
  /* The two switches are COUPLED, because the database CHECK couples them: you
     cannot invite to a route that cannot sign in. Turning accounts off therefore
     turns invitations off with it, which makes the forbidden combination
     unrepresentable in the draft rather than a 400 the administrator has to read
     about after submitting. */
  const editLocalAccounts = (enabled: boolean): void =>
    edit(
      enabled
        ? { localAccountsEnabled: true }
        : { localAccountsEnabled: false, localInvitationsEnabled: false },
    );

  const save = useMutation({
    mutationFn: async ({
      policy: next,
      scope: origin,
      revision,
    }: ScopedDraft) => {
      /* The revision the DRAFT was derived from, carried in the variables. Not
         `policyQuery.data.revision`: that is whatever the latest refetch left
         behind, which is not necessarily what the administrator saw. */
      const updated = await updateAccessPolicy(
        {
          localAccountsEnabled: next.localAccountsEnabled,
          localInvitationsEnabled: next.localInvitationsEnabled,
          invitationLifetimeHours: Number(next.invitationLifetimeHours.trim()),
        },
        revision,
      );
      return { updated, origin };
    },
    onSuccess: ({
      updated,
      origin,
    }: {
      updated: AccessPolicy;
      origin: SessionScope;
    }) => {
      /* The save is COMMITTED and belongs to the tenant, not to the session that
         submitted it — the same split OrganizationAdmin learned the hard way.
         Read the live snapshot rather than a render closure so the check cannot
         lag. */
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (!sameSession(origin, live)) {
        if (origin !== null && origin.tenantId === live?.tenantId) {
          void queryClient.invalidateQueries({
            queryKey: accessPolicyQueryKey(live?.tenantId),
          });
        }
        return;
      }
      queryClient.setQueryData(accessPolicyQueryKey(origin?.tenantId), updated);
      setDraft(null);
    },
    onError: (error, { scope: origin }) => {
      /* A conflict means another administrator saved first, so what is on screen
         is describing a policy that no longer exists. Refetch, and DISCARD the
         draft.

         Discarding is the load-bearing half, and review is why it is here.
         Keeping the draft leaves two bad options: carry its original revision and
         every retry conflicts forever, or re-stamp it from the refetch and the
         next save writes stale values over the change that just landed. Both are
         worse than losing an unsaved edit — and the second is the silent
         overwrite the `revision` column exists to prevent, reintroduced by the
         handler meant to enforce it.

         So the invariant is: the values on screen and the revision a save
         presents always come from the same read. The server's own message
         prescribes the same recovery — see the current settings, then apply the
         change again. */
      if (!isPolicyRevisionConflict(error)) return;
      if (origin === null || origin.tenantId !== tenantId) return;
      void queryClient.invalidateQueries({
        queryKey: accessPolicyQueryKey(tenantId),
      });
      /* Fenced to the session that submitted: another administrator's draft is
         not ours to discard. */
      setDraft((currentDraft) =>
        currentDraft !== null && sameSession(currentDraft.scope, origin)
          ? null
          : currentDraft,
      );
    },
  });
  /* A mutation's pending and error state belongs to the component, not to the
     session that started it: without this, a save begun by the previous
     administrator disables the next one's form and reports its failure over a
     form they never touched. */
  const saveIsOurs =
    save.variables !== undefined && sameSession(save.variables.scope, scope);
  const saving = save.isPending && saveIsOurs;
  const saveError = saveIsOurs ? save.error : null;
  const conflicted = saveError !== null && isPolicyRevisionConflict(saveError);

  const cancel = (): void => {
    setDraft(null);
    save.reset();
  };
  const submit = (): void => {
    if (!activeScoped || lifetimeError || !changed) return;
    save.mutate(activeScoped);
  };

  /* Unsaved-edit guard, matching OrganizationAdmin (PB-5403). The host's leave
     guard arms beforeunload and is asked before the rail changes section; the
     Members sub-tab switch that unmounts this panel asks through it too. */
  useLeaveGuard(changed);

  const disablingAccounts =
    activeDraft !== null &&
    savedDraft !== null &&
    savedDraft.localAccountsEnabled &&
    !activeDraft.localAccountsEnabled;

  return (
    <SettingsGroup
      title="Password accounts"
      description="Whether people can hold a password account in this workspace, and whether new ones may be invited. Corporate sign-in is unaffected by these settings."
    >
      {policyQuery.isPending ? (
        <Card>
          <CardBody className="flex flex-col gap-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-9 w-56" />
          </CardBody>
        </Card>
      ) : policyQuery.isError ? (
        <Card>
          <CardBody className="flex flex-col items-start gap-3">
            <Alert tone="critical" title="We couldn't load the access policy">
              {policyQuery.error?.message}
            </Alert>
            <Button
              variant="secondary"
              onClick={() => void policyQuery.refetch()}
              disabled={policyQuery.isFetching}
            >
              {policyQuery.isFetching ? "Retrying..." : "Try again"}
            </Button>
          </CardBody>
        </Card>
      ) : policy && current ? (
        <Card>
          <CardBody className="flex flex-col gap-1">
            <SettingRow
              label="Allow password accounts"
              description="While this is off, nobody signs in with a password. Existing password accounts are not deleted and nobody is signed out — but their next sign-in is refused."
              control={
                <Switch
                  checked={current.localAccountsEnabled}
                  onChange={editLocalAccounts}
                  disabled={saving}
                  aria-label="Allow password accounts"
                />
              }
            />
            <SettingRow
              label="Allow new password-account invitations"
              description={
                current.localAccountsEnabled
                  ? "Lets an admin invite someone who will set their own password, rather than signing in with a company account."
                  : "Unavailable while password accounts are off — you cannot invite someone to a route that cannot sign in."
              }
              control={
                <Switch
                  checked={current.localInvitationsEnabled}
                  onChange={(enabled) =>
                    edit({ localInvitationsEnabled: enabled })
                  }
                  disabled={saving || !current.localAccountsEnabled}
                  aria-label="Allow new password-account invitations"
                />
              }
            />
            <SettingRow
              label="Invitation lifetime"
              description="How long a new invitation stays redeemable, from one to seven whole days."
              control={
                <div className="flex flex-col items-end gap-1">
                  <div
                    className="flex items-center gap-2"
                    role="group"
                    aria-label="Invitation lifetime"
                  >
                    <Button
                      variant="secondary"
                      aria-label="Decrease invitation lifetime"
                      onClick={() =>
                        edit({
                          invitationLifetimeHours: String(
                            Math.max(
                              INVITE_LIFETIME_MIN,
                              Number(current.invitationLifetimeHours) -
                                INVITE_LIFETIME_STEP,
                            ),
                          ),
                        })
                      }
                      disabled={
                        saving ||
                        Number(current.invitationLifetimeHours) <=
                          INVITE_LIFETIME_MIN
                      }
                    >
                      &minus;
                    </Button>
                    <output
                      className="min-w-20 text-center text-body-small font-semibold text-pb-text"
                      aria-live="polite"
                    >
                      {Number(current.invitationLifetimeHours) /
                        INVITE_LIFETIME_STEP}{" "}
                      {Number(current.invitationLifetimeHours) ===
                      INVITE_LIFETIME_STEP
                        ? "day"
                        : "days"}
                    </output>
                    <Button
                      variant="secondary"
                      aria-label="Increase invitation lifetime"
                      onClick={() =>
                        edit({
                          invitationLifetimeHours: String(
                            Math.min(
                              INVITE_LIFETIME_MAX,
                              Number(current.invitationLifetimeHours) +
                                INVITE_LIFETIME_STEP,
                            ),
                          ),
                        })
                      }
                      disabled={
                        saving ||
                        Number(current.invitationLifetimeHours) >=
                          INVITE_LIFETIME_MAX
                      }
                    >
                      +
                    </Button>
                  </div>
                  {lifetimeError ? (
                    <p className="text-dense-1 text-pb-red-text">
                      {lifetimeError}
                    </p>
                  ) : null}
                </div>
              }
            />

            {/* The lockout warning. Shown while the draft is actually turning
                the control off, so it reads as a consequence of what the
                administrator just did rather than a standing caution they learn
                to ignore.

                Deliberately NOT conditional on whether THIS administrator holds
                a password account. `/api/auth/me` does not carry the
                authentication method, so answering that needs a second
                members-list call keyed on the administrator's own address — and
                that call cannot answer it at all when the session carries no
                email. A warning about an unrecoverable lockout that goes quiet
                exactly when it cannot tell is worse than one that occasionally
                addresses somebody it does not apply to, so the copy names the
                condition instead and the reader applies it. */}
            {disablingAccounts ? (
              <Alert tone="critical" title="Check you can still get back in">
                Anyone who signs in with a password will be refused at their
                next sign-in &mdash; including you, if that is how you sign in.
                Turning this back on needs an admin who can still get in, so
                make sure at least one uses corporate sign-in before you save.
              </Alert>
            ) : null}

            {conflicted ? (
              <Alert tone="watch" title="Someone else changed these settings">
                {saveError?.message} Your change was not applied, and the values
                above now show what is actually saved &mdash; make the change
                again if it still applies.
              </Alert>
            ) : saveError ? (
              <Alert tone="critical" title="Couldn't save these settings">
                {saveError.message}
              </Alert>
            ) : null}
          </CardBody>
          <CardFooter className="flex-wrap">
            <span className="text-dense-1 text-pb-text-muted">
              {/* Keyed on the ACTOR, not the timestamp: the migration's backfill
                  writes an `updated_at` while representing nobody's decision, so
                  a date alone would report those tenants as having chosen a
                  setting they never chose. */}
              {policy.updatedByUserId === null ? (
                "Using the defaults — nobody has changed these yet"
              ) : (
                <>
                  Last changed{" "}
                  <AdminTimeZoneLabel
                    label={dateTimeOr(policy.updatedAt, "—")}
                    formatter={formatter}
                    variant="inline"
                  />
                </>
              )}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={cancel}
                disabled={activeDraft === null || saving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submit}
                disabled={!changed || lifetimeError !== null || saving}
              >
                {saving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </CardFooter>
        </Card>
      ) : null}
    </SettingsGroup>
  );
}
